import React, { useState, useEffect, useCallback, useMemo } from 'react';
import productionApi from '../../services/productionApi';
import { formatQuantity } from '../../utils/formatters';
import './FinishedGoodsTab.css';

// Custom Outlined SVG Format Icons
const CanIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M5 6h14" />
        <path d="M5 18h14" />
    </svg>
);

const BottleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 3h6v3H9z" />
        <path d="M12 6v15" strokeDasharray="2 2" opacity="0.3" />
        <path d="M7 9c0-1.657 1.343-3 3-3h4c1.657 0 3 1.343 3 3v12H7V9z" />
    </svg>
);

const KegIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="7" ry="3" />
        <path d="M5 5v14c0 1.66 3.13 3 7 3s7-1.34 7-3V5" />
        <path d="M5 12c0 1.66 3.13 3 7 3s7-1.34 7-3" />
    </svg>
);

function formatRelativeTime(dateString) {
    if (!dateString) return '—';
    const diff = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

export default function FinishedGoodsTab() {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const fetchFinishedGoods = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await productionApi.listFinishedGoods();
            setItems(data);
        } catch (err) {
            console.error('Error fetching finished goods:', err);
            setError('Failed to load finished goods. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFinishedGoods();
    }, [fetchFinishedGoods]);

    // Handle search query change & reset page
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    // Filter items based on searchQuery
    const filteredItems = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return query
            ? items.filter(
                item =>
                    item.product_name.toLowerCase().includes(query) ||
                    item.output_format_name.toLowerCase().includes(query)
            )
            : items;
    }, [items, searchQuery]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredItems.slice(start, start + itemsPerPage);
    }, [filteredItems, currentPage]);

    // eslint-disable-next-line no-unused-vars
    const totalUnits = useMemo(
        () => items.reduce((sum, item) => sum + item.quantity_on_hand, 0),
        [items]
    );

    // eslint-disable-next-line no-unused-vars
    const totalProducts = useMemo(
        () => new Set(items.map(i => i.product_id)).size,
        [items]
    );

    // Resolve format type badges and outline icons
    const getFormatConfig = (formatKey) => {
        const key = (formatKey || '').toLowerCase();
        if (key.includes('bottle') || key.includes('16oz') || key.includes('32oz')) {
            return {
                label: 'Bottles',
                className: 'format-bottles',
                icon: <BottleIcon />
            };
        }
        if (key.includes('can') || key.includes('rtd')) {
            return {
                label: 'Cans',
                className: 'format-cans',
                icon: <CanIcon />
            };
        }
        if (key.includes('gallon') || key.includes('concentrate')) {
            return {
                label: 'Gallons',
                className: 'format-gallons',
                icon: <BottleIcon />
            };
        }
        if (key.includes('keg')) {
            return {
                label: 'Kegs',
                className: 'format-kegs',
                icon: <KegIcon />
            };
        }
        return {
            label: 'Other',
            className: 'format-other',
            icon: <BottleIcon />
        };
    };

    if (isLoading) {
        return (
            <div className="fg-loading">
                {[1, 2, 3].map(i => (
                    <div key={i} className="fg-skeleton-row" />
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="fg-error">
                <p>{error}</p>
                <button className="btn btn-primary" onClick={fetchFinishedGoods}>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="finished-goods-tab">
            {/* Controls Bar */}
            <div className="fg-controls-bar">
                <div className="search-box">
                    <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search product or format..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                    {searchQuery && (
                        <button className="clear-btn" onClick={() => { setSearchQuery(''); setCurrentPage(1); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="controls-right">
                    <button className="fg-filter-btn" title="Filters">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                    </button>
                    <button className="fg-refresh-btn" onClick={fetchFinishedGoods} title="Refresh">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                        </svg>
                    </button>
                </div>
            </div>

            {filteredItems.length > 0 && (
                <div className="fg-table-container">
                    {/* List Header */}
                    <div className="fg-list-header">
                        <div className="fg-header-col col-product">PRODUCT / FORMAT</div>
                        <div className="fg-header-col col-qty">QTY ON HAND</div>
                        <div className="fg-header-col col-unit">UNIT</div>
                        <div className="fg-header-col col-updated">LAST UPDATED</div>
                        <div className="fg-header-col col-actions">ACTION</div>
                    </div>

                    {/* Flat Row List */}
                    <div className="fg-list-rows">
                        {paginatedItems.map(item => {
                            const cfg = getFormatConfig(item.output_format);
                            const inStock = item.quantity_on_hand > 0;

                            return (
                                <div key={item.id} className={`fg-row-item ${cfg.className}`}>
                                    {/* Product / format info with icon */}
                                    <div className="fg-product-col">
                                        <div className="fg-icon-container">
                                            {cfg.icon}
                                        </div>
                                        <div className="fg-info-group">
                                            <div className="fg-title-row">
                                                <span className="fg-product-name">{item.product_name}</span>
                                                <span className="fg-format-badge">{cfg.label}</span>
                                            </div>
                                            <span className="fg-format-name">{item.output_format_name}</span>
                                        </div>
                                    </div>

                                    {/* Quantity on Hand & stock indicator */}
                                    <div className="fg-qty-group">
                                        <span className="fg-qty-value">
                                            {formatQuantity(item.quantity_on_hand, item.output_unit)}
                                        </span>
                                        <span className={`fg-status-indicator ${inStock ? 'in-stock' : 'out-of-stock'}`}>
                                            <span className="status-dot">●</span>
                                            {inStock ? 'In Stock' : 'Out of Stock'}
                                        </span>
                                    </div>

                                    {/* Unit */}
                                    <div className="fg-unit-group">
                                        <span className="fg-unit-value">{item.output_unit}</span>
                                    </div>

                                    {/* Last Updated */}
                                    <div className="fg-updated-group">
                                        <span className="fg-updated-value">
                                            {formatRelativeTime(item.last_updated)}
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="fg-actions-group">
                                        <button className="fg-row-action-btn" title="More options">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="5" r="1" />
                                                <circle cx="12" cy="12" r="1" />
                                                <circle cx="12" cy="19" r="1" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {filteredItems.length === 0 && (
                <div className="fg-empty">
                    <div className="fg-empty-icon">📦</div>
                    <h3>No finished goods</h3>
                    <p>There are currently no finished goods available in inventory.</p>
                </div>
            )}

            {/* Pagination */}
            {filteredItems.length > 0 && (
                <div className="fg-pagination-container">
                    <span className="fg-pagination-info">
                        Showing {filteredItems.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} products
                    </span>
                    <div className="fg-pagination-controls">
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            ‹
                        </button>
                        <span className="pagination-page-num active">{currentPage}</span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
